import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Loader2,
  Tag,
  Users,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { SpecialistTypeFormPanel } from '../components/specialists/SpecialistTypeFormPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const SpecialistTypesPage = () => {
  const { t } = useTranslation('specialists');
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);

  useEffect(() => {
    fetchTypes();
  }, [search]);

  const fetchTypes = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API_URL}/api/specialist-types?${params.toString()}`);
      setTypes(response.data);
    } catch (error) {
      console.error('Error fetching types:', error);
      toast.error(t('types.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data) => {
    try {
      await axios.post(`${API_URL}/api/specialist-types`, data);
      toast.success(t('types.messages.created'));
      fetchTypes();
    } catch (error) {
      const message = error.response?.data?.detail;
      toast.error(typeof message === 'string' ? message : t('types.messages.saveError'));
      throw error;
    }
  };

  const handleUpdate = async (data) => {
    try {
      await axios.put(`${API_URL}/api/specialist-types/${editingType.id}`, data);
      toast.success(t('types.messages.updated'));
      fetchTypes();
    } catch (error) {
      const message = error.response?.data?.detail;
      toast.error(typeof message === 'string' ? message : t('types.messages.saveError'));
      throw error;
    }
  };

  const handleDelete = async () => {
    try {
      const response = await axios.delete(`${API_URL}/api/specialist-types/${typeToDelete.id}`);
      toast.success(response.data.message || t('types.messages.deleted'));
      fetchTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('types.messages.deleteError'));
    } finally {
      setDeleteDialogOpen(false);
      setTypeToDelete(null);
    }
  };

  const openEdit = (type) => {
    setEditingType(type);
    setIsFormOpen(true);
  };

  const openDelete = (type) => {
    setTypeToDelete(type);
    setDeleteDialogOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingType(null);
  };

  return (
    <div className="space-y-6" data-testid="specialist-types-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 font-['Work_Sans',sans-serif]">
            {t('types.title')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t('types.subtitle')}
          </p>
        </div>
        <Button 
          className="bg-[#E63946] hover:bg-[#C92A36]"
          onClick={() => setIsFormOpen(true)}
          data-testid="add-type-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('types.newType')}
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={t('types.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="type-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Types Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#E63946]" />
        </div>
      ) : types.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Tag className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">{t('types.noTypes')}</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">{t('types.noTypesSubtitle')}</p>
            <Button 
              className="bg-[#E63946] hover:bg-[#C92A36]"
              onClick={() => setIsFormOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('types.newType')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {types.map((type) => (
            <Card key={type.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${type.color}20` }}
                    >
                      <Tag className="h-5 w-5" style={{ color: type.color }} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{type.name}</h3>
                      <p className="text-sm text-gray-500">{type.code}</p>
                    </div>
                  </div>
                  <Badge 
                    variant={type.status === 'active' ? 'default' : 'secondary'}
                    className={type.status === 'active' ? 'bg-green-100 text-green-800' : ''}
                  >
                    {type.status === 'active' ? 'Activo' : 'Inactivo'}
                  </Badge>
                </div>
                
                {type.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{type.description}</p>
                )}
                
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <Users className="h-4 w-4" />
                    <span>{type.specialists_count} especialistas</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(type)}
                      data-testid={`edit-type-${type.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => openDelete(type)}
                      data-testid={`delete-type-${type.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Panel */}
      <SpecialistTypeFormPanel
        specialistType={editingType}
        isOpen={isFormOpen}
        onClose={closeForm}
        onSubmit={editingType ? handleUpdate : handleCreate}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Confirmar Eliminación
            </DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas eliminar el tipo "{typeToDelete?.name}"?
              {typeToDelete?.specialists_count > 0 && (
                <span className="block mt-2 text-orange-600">
                  Este tipo tiene {typeToDelete.specialists_count} especialistas asignados y será desactivado en lugar de eliminado.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              data-testid="confirm-delete-btn"
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SpecialistTypesPage;
